import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import OrderDetail from '../components/OrderDetail';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateUtils';
import { API_URL } from '../config';
import axios from 'axios';

function OrganizationOrders() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  
  const [organization, setOrganization] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderTypeTab, setOrderTypeTab] = useState(0); // 0 = Ad Orders, 1 = School Orders
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);

  // Fetch organization details and all orders
  const fetchOrganizationOrders = useCallback(async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      const token = await getToken();
      
      // Fetch organization details
      const orgResponse = await axios.get(`${API_URL}/organizations/${organizationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // The organizations API doesn't wrap response in success/data structure
      if (orgResponse.data) {
        setOrganization(orgResponse.data);
      }

      // Fetch all orders for this organization (both active and inactive)
      const ordersResponse = await axios.get(`${API_URL}/orders/organization/${organizationId}?includeInactive=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (ordersResponse.data.success) {
        const ordersData = ordersResponse.data.data || [];
        setOrders(ordersData);
      }
    } catch (error) {
      console.error('Error fetching organization orders:', error);
      setError('Failed to load organization orders');
    } finally {
      setLoading(false);
    }
  }, [organizationId, getToken]);

  useEffect(() => {
    fetchOrganizationOrders();
  }, [fetchOrganizationOrders]);

  // Filter orders by type
  const filterAdOrders = (orders) => {
    return orders.filter(order => order.orderType === 'ad' || order.order_type === 'ad');
  };

  const filterSchoolOrders = (orders) => {
    return orders.filter(order => 
      (order.orderType === 'school' || order.order_type === 'school') &&
      (!order.status || order.status.toLowerCase() !== 'cancelled')
    );
  };

  // Calculate variables first
  const adOrders = filterAdOrders(orders);
  const schoolOrders = filterSchoolOrders(orders);
  
  // Determine if we should show school orders tab (only for school organizations)
  const isSchoolOrganization = organization?.businessType?.toLowerCase() === 'school';
  const showSchoolOrdersTab = isSchoolOrganization && schoolOrders.length > 0;

  const handleOrderTypeTabChange = (event, newValue) => {
    // Only allow tab change if school orders tab is available
    if (newValue === 1 && !showSchoolOrdersTab) {
      return;
    }
    setOrderTypeTab(newValue);
  };

  // Reset tab to 0 if school orders tab is not available
  useEffect(() => {
    if (!showSchoolOrdersTab && orderTypeTab === 1) {
      setOrderTypeTab(0);
    }
  }, [showSchoolOrdersTab, orderTypeTab]);

  const handleBackToOrganizations = () => {
    // Navigate back to organizations and set the selected organization
    // This will open the organization details automatically
    navigate('/app/organizations', { 
      state: { selectedOrganizationId: organizationId } 
    });
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setOrderDetailOpen(true);
  };

  const handleCloseOrderDetail = () => {
    setOrderDetailOpen(false);
    setSelectedOrder(null);
  };

  // Helper function to calculate order value from audiences
  const calculateOrderValue = (order) => {
    // Calculate total from audiences if available
    if (order.audiences && Array.isArray(order.audiences) && order.audiences.length > 0) {
      const total = order.audiences.reduce((sum, audience) => {
        const price = parseFloat(audience.price || 0);
        const discount = parseFloat(audience.discount || 0);
        const discountType = audience.discountType || '$';
        
        let subtotal;
        if (discount === 0) {
          subtotal = price;
        } else if (discountType === '%') {
          // Percentage discount: subtract percentage of price
          const discountAmount = (price * discount) / 100;
          subtotal = Math.max(0, price - discountAmount);
        } else {
          // Dollar discount: subtract fixed amount
          subtotal = Math.max(0, price - discount);
        }
        
        return sum + subtotal;
      }, 0);
      
      if (total > 0) {
        return total.toFixed(2);
      }
    }
    // Fall back to totalPrice or value fields
    return order.totalPrice || order.total_price || '0.00';
  };

  // Format the order value as currency
  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'New': 'info',
      'pending': 'warning',
      'processing': 'info',
      'completed': 'success',
      'delivered': 'success',
      'cancelled': 'error',
      'prospect': 'default',
      'started': 'info',
      'admin-approval': 'warning'
    };
    return statusColors[status] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackToOrganizations} sx={{ mt: 2 }}>
          Back to Organisations
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBackToOrganizations} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            All Orders - {organization?.name || 'Loading...'}
          </Typography>
          {organization && (
            <Typography variant="body2" color="text.secondary">
              {organization.businessType} � {organization.address}, {organization.suburb} {organization.state} {organization.postcode}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Order Type Tabs */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Tabs 
            value={orderTypeTab} 
            onChange={handleOrderTypeTabChange}
            variant={showSchoolOrdersTab ? "fullWidth" : "standard"}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab 
              label={`Advertising Orders (${adOrders.length})`}
              id="ad-orders-tab"
              aria-controls="ad-orders-tabpanel"
            />
            {showSchoolOrdersTab && (
              <Tab 
                label={`School Orders (${schoolOrders.length})`}
                id="school-orders-tab"
                aria-controls="school-orders-tabpanel"
              />
            )}
          </Tabs>
        </CardContent>
      </Card>

      {/* Orders Content */}
      <Card>
        <CardContent>
          {/* Advertising Orders Tab */}
          {orderTypeTab === 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Advertising Orders ({adOrders.length})
              </Typography>
              
              {adOrders.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Order ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Product Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>School</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Active</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Total Price</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Sales Person</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Created Date</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adOrders.map((order) => (
                        <TableRow key={order.id} hover>
                          <TableCell>#{order.id}</TableCell>
                          <TableCell>{order.productType || order.product_type || 'N/A'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="info.main" fontWeight={500}>
                              {order.schoolName || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={order.status || 'New'}
                              color={getStatusColor(order.status)}
                              size="small"
                              sx={{ 
                                height: '24px', 
                                fontWeight: 500,
                                borderRadius: '4px'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={order.active === false ? "Inactive" : "Active"}
                              color={order.active === false ? "error" : "success"}
                              size="small"
                              sx={{ 
                                height: '24px', 
                                fontWeight: 500,
                                borderRadius: '4px'
                              }}
                            />
                          </TableCell>
                          <TableCell>{formatCurrency(calculateOrderValue(order))}</TableCell>
                          <TableCell>{order.salesPersonName || order.sales_person_name || 'Not assigned'}</TableCell>
                          <TableCell>{order.createdAt ? formatDate(order.createdAt) : 'N/A'}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small"
                                onClick={() => handleViewOrder(order)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No advertising orders found for this organization.
                </Alert>
              )}
            </Box>
          )}

          {/* School Orders Tab */}
          {showSchoolOrdersTab && orderTypeTab === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                School Orders ({schoolOrders.length})
              </Typography>
              
              {schoolOrders.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Order ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Active</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Total Price</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Created Date</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {schoolOrders.map((order) => (
                        <TableRow key={order.id} hover>
                          <TableCell>#{order.id}</TableCell>
                          <TableCell>
                            <Chip 
                              label={order.status || 'Pending'}
                              color={getStatusColor(order.status)}
                              size="small"
                              sx={{ 
                                height: '24px', 
                                fontWeight: 500,
                                borderRadius: '4px'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={order.active === false ? "Inactive" : "Active"}
                              color={order.active === false ? "error" : "success"}
                              size="small"
                              sx={{ 
                                height: '24px', 
                                fontWeight: 500,
                                borderRadius: '4px'
                              }}
                            />
                          </TableCell>
                          <TableCell>{formatCurrency(calculateOrderValue(order))}</TableCell>
                          <TableCell>{order.createdAt ? formatDate(order.createdAt) : 'N/A'}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small"
                                onClick={() => handleViewOrder(order)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No school orders found for this organization.
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <OrderDetail
          open={orderDetailOpen}
          onClose={handleCloseOrderDetail}
          order={selectedOrder}
        />
      )}
    </Box>
  );
}

export default OrganizationOrders;